import * as React from "react";
import * as apiSubscriptions from "../../../api/subscriptions";
import * as apiUserGroups from "../../../api/userGroups";
import { Alert, AlertType } from "../../../components/alert";
import { Button, ButtonType } from "../../../components/button";
import { CheckBox } from "../../../components/checkbox";
import { ComboBox, ComboBoxType, DropDownTextItem, DROPDOWN_TEXT_ITEM_HEIGHT } from "../../../components/comboBox";
import { FilterCriteria } from "../../../components/filterCriteria";
import { FormGroup } from "../../../components/form";
import { InputSize } from "../../../components/input";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ScrollBarVisibility, ScrollViewer } from "../../../components/scrollViewer";
import { TextBox } from "../../../components/textBox";
import { Window } from "../../../components/window";
import * as globals from "../../../globals";
import * as modelsGet from "../../../models/get";
import { ISubscriptionFilter } from "../../../models/get";
import * as utils from "../../../utils/utils";
import { RadioButton } from "../../../components/radiobutton";
import { LabelContainer, LabelColor, Label } from "../../../components/label";

enum Mode
{
    create,
    edit
}

enum SubscriberTypes {
    Group = "Group",
    User = "User",
    Calendar = "Calendar",
    Email = "Email"
}

interface Subscriber {
    id?: number;
    name?: string;
    type?: SubscriberTypes;
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    subscriptionId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    currentUser?: modelsGet.IUser;
    isLoading?: boolean;
    mode?: Mode;
    subscription?: modelsGet.ISubscription;
    allUserGroups?: modelsGet.IUserGroup[];
    userGroups?: modelsGet.IUserGroup[];
    allCalendars?: modelsGet.ICalendar[];
    calendars?: modelsGet.ICalendar[];
    templates?: modelsGet.ITemplate[];
    form?: {
        description?: string;
        subscriber?: string;
        userGroups?: number[];
        calendars?: number[];
        template?: modelsGet.ITemplate;
        actionCreate?: boolean;
        actionChange?: boolean;
        actionDelete?: boolean;
        selectGroups?: boolean;
        selectCalendars?: boolean;
        selectEmail?: boolean;
        filters?: ISubscriptionFilter[];
    },
    errors?: Object;
    alertMessage?: string;
    alertType?: AlertType;
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;
    
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.subscriptionId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    private async initEditModeAsync(props: IProperties)
    {
        const currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();
        const userGroups = await apiUserGroups.getUserGroups();
        const calendars = await globals.getCalendarsAsync();
        const templates = await globals.getTemplatesAsync();
        const subscription = await apiSubscriptions.getSubscription(props.subscriptionId);

        // Werte setzen.
        this.setState((state: IState) =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.currentUser = currentUser;
            newState.subscription = subscription;
            newState.userGroups = userGroups.where(ug => !subscription.subscribers.any(s => s.subscriber_type === SubscriberTypes.Group && s.subscriber === ug.id));
            newState.allUserGroups = [...userGroups];
            newState.allCalendars = [...calendars];
            newState.calendars = calendars.where(c => !subscription.subscribers.any(s => s.subscriber_type === SubscriberTypes.Calendar && s.subscriber === c.id));
            newState.templates = templates.where(t => !t.is_list);

            newState.form = {
                subscriber: subscription.subscribers.where(s => s.subscriber_type === SubscriberTypes.Email).select(s => s.subscriber_email).first(),
                userGroups: subscription.subscribers.where(s => s.subscriber_type === SubscriberTypes.Group).select(s => s.subscriber),
                calendars: subscription.subscribers.where(s => s.subscriber_type === SubscriberTypes.Calendar).select(s => s.subscriber),
                selectGroups: subscription.subscribers.any(s => s.subscriber_type === SubscriberTypes.Group),
                selectCalendars: subscription.subscribers.any(s => s.subscriber_type === SubscriberTypes.Calendar),
                selectEmail: subscription.subscribers.any(s => s.subscriber_type === SubscriberTypes.Email),
                description: subscription.description,
                template: templates.where(t => t.id === subscription.template_id).first() || {},
                actionCreate: subscription.action_created,
                actionChange: subscription.action_changed,
                actionDelete: subscription.action_deleted,
                filters: newState.subscription.filters ? [...newState.subscription.filters] : []
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }
    
    private async initCreateModeAsync(props: IProperties)
    {
        const currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();
        const userGroups = await apiUserGroups.getUserGroups();
        const calendars = await globals.getCalendarsAsync();
        const templates = await globals.getTemplatesAsync();

        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.currentUser = currentUser;
            newState.subscription = {
                filters: []
            };
            newState.userGroups = userGroups;
            newState.allUserGroups = [...userGroups];
            newState.allCalendars = [...calendars];
            newState.calendars = calendars;
            newState.templates = templates.where(t => !t.is_list);

            newState.form = {
                template: {},
                userGroups: [],
                calendars: [],
                filters: [...newState.subscription.filters]
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.description !== nextState.form.description)
                this._isSaveButtonDisabled = false;

            if (!Object.equals(form.userGroups, nextState.form.userGroups))
                this._isSaveButtonDisabled = false;

            if (!Object.equals(form.calendars, nextState.form.calendars))
                this._isSaveButtonDisabled = false;

            if (form.subscriber !== nextState.form.subscriber)
                this._isSaveButtonDisabled = false;

            if (form.template.id !== nextState.form.template.id)
                this._isSaveButtonDisabled = false;

            if (form.actionCreate !== nextState.form.actionCreate)
                this._isSaveButtonDisabled = false;

            if (form.actionChange !== nextState.form.actionChange)
                this._isSaveButtonDisabled = false;

            if (form.actionDelete !== nextState.form.actionDelete)
                this._isSaveButtonDisabled = false;

            if (!Object.equals(form.filters, nextState.form.filters))
                this._isSaveButtonDisabled = false;
        }
    }

    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, subscription, form } = this.state;

            var newData: modelsGet.ISubscription = {};
            newData.description = form.description;
            newData.subscribers = [];

            if (form.selectGroups)
            {
                form.userGroups.forEach(u => newData.subscribers.push({
                    subscriber: u,
                    subscriber_type: SubscriberTypes.Group
                }));
            }
            else if (form.selectCalendars)
            {
                form.calendars.forEach(c => newData.subscribers.push({
                    subscriber: c,
                    subscriber_type: SubscriberTypes.Calendar
                }));
            }
            else if (form.selectEmail)
            {
                newData.subscribers = [{
                    subscriber: 0,
                    subscriber_email: form.subscriber,
                    subscriber_type: SubscriberTypes.Email
                }];
            }
            
            newData.action_created = form.actionCreate;
            newData.action_changed = form.actionChange;
            newData.action_deleted = form.actionDelete;
            newData.template_id = form.template && form.template.id;
            newData.created_at = subscription.created_at;
            newData.created_by = subscription.created_by;
            newData.changed_at = subscription.changed_at;
            newData.changed_by = subscription.changed_by;
            newData.filters = form.filters;

            if (mode === Mode.edit)
            {
                await apiSubscriptions.put(subscription.id, newData);
            }
            else
            {
                await apiSubscriptions.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { subscription } = this.state;

            await apiSubscriptions.remove(subscription.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    private onComboBoxChanged = (selectedItem: any, formItemName: string) => {
        this.setState((state: IState) => {
            state.form[formItemName] = selectedItem;
            return state;
        });
    }

    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) => {
            state.form[name] = value;
            return state;
        });
    }

    private onRadioChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form.selectGroups = false;
            state.form.selectCalendars = false;
            state.form.selectEmail = false;
            state.form[name] = value;
            return state;
        });
    }

    private onNewFilterCriteriaClause = (index?: number) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];
        const filter: ISubscriptionFilter = {
            id: -newFilters.length,
            value: "text " + (-newFilters.length)
        };
        const hasItems: boolean = newFilters.length > 0;

        if (hasItems && (index == null || index > 0))
        {
            filter.sentential_operator = "And";
        }
        else if (hasItems && index === 0)
        {
            newFilters[0].sentential_operator = "And";
        }

        if (index != null)
        {
            newFilters.splice(index, 0, filter);
        }
        else
        {
            newFilters.push(filter);
        }

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private onRemoveFilterCriteriaClause = (index: number) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];
        newFilters.splice(index, 1);

        if (newFilters.length > 0 && index === 0)
            newFilters[0].sentential_operator = undefined;

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private onClauseChange = (item: ISubscriptionFilter) =>
    {
        const { form } = this.state;
        const newFilters = [...form.filters];

        for (var i = 0; i < newFilters.length; i++)
        {
            if (newFilters[i].id === item.id)
            {
                newFilters[i] = { ...item };
                break;
            }
        }

        this.setState((state: IState) =>
        {
            state.form.filters = newFilters;
            return state;
        });
    }

    private onCalendarsChange = (selectedItem: modelsGet.ICalendar) =>
    {
        const { calendars, form } = this.state;
        const newCalendars = [...calendars.where(s => s.id !== selectedItem.id)];
        const newCalendarsSelected = [...form.calendars, selectedItem.id];

        this.setState((state: IState) =>
        {
            state.calendars = newCalendars.sort((a, b) =>
            {
                if (a.name < b.name) { return -1; }
                if (a.name > b.name) { return 1; }
                return 0;
            });
            state.form.calendars = newCalendarsSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onCalendarsRemove = (calendar: modelsGet.ICalendar) =>
    {
        const { calendars, form } = this.state;
        const newCalendars = [...calendars, { ...calendar }];
        const newCalendarsSelected = [...form.calendars.where(s => s !== calendar.id)];

        this.setState((state: IState) =>
        {
            state.calendars = newCalendars.sort((a, b) =>
            {
                if (a.name < b.name) { return -1; }
                if (a.name > b.name) { return 1; }
                return 0;
            });
            state.form.calendars = newCalendarsSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onUserGroupsChange = (selectedItem: modelsGet.IUserGroup) =>
    {
        const { userGroups, form } = this.state;
        const newGroups = [...userGroups.where(s => s.id !== selectedItem.id)];
        const newGroupsSelected = [...form.userGroups, selectedItem.id];

        this.setState((state: IState) =>
        {
            state.userGroups = newGroups.sort((a, b) =>
            {
                if (a.name < b.name) { return -1; }
                if (a.name > b.name) { return 1; }
                return 0;
            });
            state.form.userGroups = newGroupsSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onUserGroupsRemove = (group: modelsGet.IUserGroup) =>
    {
        const { userGroups, form } = this.state;
        const newGroups = [...userGroups, { ...group }];
        const newGroupsSelected = [...form.userGroups.where(s => s !== group.id)];

        this.setState((state: IState) =>
        {
            state.userGroups = newGroups.sort((a, b) =>
            {
                if (a.name < b.name) { return -1; }
                if (a.name > b.name) { return 1; }
                return 0;
            });
            state.form.userGroups = newGroupsSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private renderContent(): JSX.Element
    {
        const {
            mode,
            subscription,
            allUserGroups,
            userGroups,
            allCalendars,
            calendars,
            templates,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Beschreibung"
                            value={form.description}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["Description"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "description")}
                        />
                        <label style={{
                            margin: "5px 0"
                        }}>Abonnenten</label>
                        <RadioButton
                            style={{
                                marginTop: "5px"
                            }}
                            name="test"
                            label="Gruppen"
                            isChecked={form.selectGroups}
                            onCheckedChanged={(e) => this.onRadioChanged(e, "selectGroups")}
                        />
                        <ComboBox
                            style={{
                                marginLeft: 28
                            }}
                            canRemoveSelection={true}
                            dataSource={userGroups}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            onSelectionChanged={(data) => this.onUserGroupsChange(data)}
                            valueMember="id"
                            displayMember="name"
                            isDisabled={!form.selectGroups || (!userGroups || userGroups.length === 0)}>
                            {
                                (item: modelsGet.IUserGroup) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        {
                            form.userGroups && form.userGroups.length > 0 &&
                            <LabelContainer
                                style={{
                                    marginLeft: 28
                                }}>
                                {
                                    allUserGroups.where(s => form.userGroups.contains(s.id)).map((data) =>
                                    {
                                        return (
                                            <Label
                                                dataSource={data}
                                                text={data.name}
                                                isButtonVisible={true}
                                                color={LabelColor.blue}
                                                onButtonClick={this.onUserGroupsRemove} />
                                        );
                                    })
                                }
                            </LabelContainer>
                        }
                        <RadioButton
                            style={{
                                marginTop: "5px"
                            }}
                            name="test"
                            label="Kalender"
                            isChecked={form.selectCalendars}
                            onCheckedChanged={(e) => this.onRadioChanged(e, "selectCalendars")}
                        />
                        <ComboBox
                            style={{
                                marginLeft: 28
                            }}
                            canRemoveSelection={true}
                            dataSource={calendars}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            onSelectionChanged={(data) => this.onCalendarsChange(data)}
                            valueMember="id"
                            displayMember="name"
                            isDisabled={!form.selectCalendars || (!calendars || calendars.length === 0)}>
                            {
                                (item: modelsGet.ICalendar) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        {
                            form.calendars && form.calendars.length > 0 &&
                            <LabelContainer
                                style={{
                                    marginLeft: 28
                                }}>
                                {
                                    allCalendars.where(s => form.calendars.contains(s.id)).map((data) =>
                                    {
                                        return (
                                            <Label
                                                dataSource={data}
                                                text={data.name}
                                                isButtonVisible={true}
                                                color={LabelColor.blue}
                                                onButtonClick={this.onCalendarsRemove} />
                                        );
                                    })
                                }
                            </LabelContainer>
                        }
                        <RadioButton
                            style={{
                                marginTop: "5px"
                            }}
                            name="test"
                            label="Benutzerdefinierte E-Mail"
                            isChecked={form.selectEmail}
                            onCheckedChanged={(e) => this.onRadioChanged(e, "selectEmail")}
                        />
                        <TextBox
                            style={{
                                marginLeft: 28
                            }}
                            value={form.subscriber}
                            size={InputSize.normal}
                            spellCheck={false}
                            autoFocus={false}
                            isDisabled={!form.selectEmail}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "subscriber")}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label style={{
                            margin: "5px 0"
                        }}>Aktion</label>
                        <CheckBox
                            label="Erstellen"
                            isChecked={form.actionCreate}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionCreate")}
                        />
                        <CheckBox
                            label="Bearbeiten"
                            isChecked={form.actionChange}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionChange")}
                        />
                        <CheckBox
                            label="Löschen"
                            isChecked={form.actionDelete}
                            onCheckedChanged={(e) => this.onCheckboxChanged(e, "actionDelete")}
                        />
                    </FormGroup>
                    <FormGroup>
                        <ComboBox
                            label="Dokument"
                            dataSource={templates}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.template.name}
                            selectedValue={form.template.id}
                            onSelectionChanged={(data) => this.onComboBoxChanged(data, "template")}
                            valueMember="id"
                            displayMember="name"
                            hint="Wählen Sie ein Dokument aus welches an die E-Mail der Benachrichtigung angehängt werden soll.">
                            {
                                (item: modelsGet.ITemplate) => {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                        <FilterCriteria
                            label="Filterkriterien"
                            filters={form.filters}
                            isGlobalRule={true}
                            onNewClause={this.onNewFilterCriteriaClause}
                            onRemoveClause={this.onRemoveFilterCriteriaClause}
                            onClauseChange={this.onClauseChange}
                        />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            subscription,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Benachrichtigungsregel erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Benachrichtigungsregel "${subscription.description}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}